import { useEffect, useState } from "react";
import cn from "classnames";
import { IconChevronDown } from "@tabler/icons";
import {
  ColumnSizeProps,
  Group,
  InputProps,
  Label,
  SelectOption,
} from "@fdoh/health-react";
import { useClickOutside } from "../hooks";
import { strip, useGroupProps } from "./Select2.types";
import { ControllerFieldState, UseFormSetValue } from "react-hook-form";
import "../../scss/_select.scss";

export type Select2Props = Omit<InputProps, "defaultValue | type | value"> &
  ColumnSizeProps & {
    value: string | number;
    setValue: UseFormSetValue<any>;
    options: SelectOption[];
    state: ControllerFieldState;
  };

export const Select2 = (props: Select2Props) => {
  const groupProps = useGroupProps(props);
  const { onChange, onBlur, value, name, state, ...selectProps } = props;
  const { options, label, setValue } = selectProps;
  const [show, setShow] = useState(false);
  const [optionList, setOptionList] = useState<SelectOption[]>(options);
  const [userInput, setUserInput] = useState<string>();
  const menuRef = useClickOutside(() => setShow(false));

  const toggleOptions = () => setShow(!show);

  const handleChange = (query: string) => {
    // If value was already set, unset it.
    if (value !== 0) setValue(name, 0);

    // Filter the options list by the typed value.
    const updatedList = options.filter((x) =>
      strip(x.name).includes(strip(query))
    );

    // Update the options list.
    setOptionList(updatedList);

    // Update the entry to match the value typed in by the user.
    setUserInput(query);
  };

  const handleSelected = (value: SelectOption) => {
    setValue(name, value.id, { shouldValidate: true });
    setUserInput(value.name);
  };

  useEffect(() => {
    if (options) {
      setOptionList(options);

      let select = options.find((x) => x.id === value);
      setUserInput(select?.name ?? "");
    }
  }, [options, value]);

  return (
    <Group {...groupProps}>
      <Label label={label} name={name} required={props.required} />
      <div
        className={cn("dropdown", { "is-invalid": state.invalid })}
        ref={menuRef}
        onClick={() => toggleOptions()}
      >
        <div
          className={cn(
            "input-group",
            "input-group-flat",
            "input-group-select2",
            {
              "is-invalid": state.invalid,
            }
          )}
        >
          <input
            autoComplete="off"
            className="form-control"
            value={userInput}
            onChange={(e) => handleChange(e.target.value)}
            // onKeyUp={handleKeyUp}
          />
          <span className="input-group-text">
            <span>
              <IconChevronDown />
            </span>
          </span>
        </div>
        {show && (
          <div className={cn("dropdown-menu", "select2", "show")}>
            {optionList?.map((v, i) => (
              <div
                className="dropdown-item"
                key={i}
                onClick={() => handleSelected(v)}
                tabIndex={0}
              >
                {<span>{v.name}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
      {state.invalid && (
        <div className="invalid-feedback">{state.error?.message}</div>
      )}
    </Group>
  );
};

export default Select2;
